<template>
  <div class="footprint-page">
    <!-- 页面头部 -->
    <div class="page-header">
      <i class="icon-time"></i>
      <span>我的足迹</span>
    </div>

    <!-- 无足迹提示 -->
    <div class="empty-footprint" v-if="!hasFootprints">
      <img src="https://picsum.photos/seed/empty/200/200" alt="暂无足迹" class="empty-img">
      <p>您还没有浏览足迹</p>
      <button class="explore-btn" @click="goExplore">去逛逛</button>
    </div>

    <!-- 主内容区：左侧时间轴 + 右侧足迹列表 -->
    <div class="main-container" v-else>
      <!-- 左侧时间轴 -->
      <div class="time-axis">
        <div class="time-item" v-for="period in timePeriods" :key="period.key"
          :class="{ active: selectedPeriod === period.key }" @click="selectPeriod(period.key)">
          <div class="time-dot"></div>
          <div class="time-text">{{ period.label }}</div>
        </div>
      </div>

      <!-- 右侧足迹列表 -->
      <div class="footprint-content">
        <!-- 显示当前选中时间段的标题 -->
        <div class="date-label" v-if="filteredFootprints.length">
          {{ getCurrentPeriodLabel() }}
        </div>

        <!-- 暂无数据提示 -->
        <div class="no-data-tip" v-if="!filteredFootprints.length">
          <p>该时间段暂无浏览足迹</p>
        </div>

        <!-- 模型网格 -->
        <div class="model-grid" v-if="filteredFootprints.length">
          <div class="model-item" v-for="(item, index) in filteredFootprints" :key="item.modelId + index"
            @click="goToDetail(item.modelId)">
            <img :src="item.previewUrl || 'https://picsum.photos/seed/default/200/200'" :alt="item.title || '模型图片'"
              class="model-img">
            <div class="model-meta">
              <span class="model-type">模型</span>
              <span class="model-name">{{ item.title || '未命名模型' }}</span>
            </div>
            <!-- 根据时间段显示不同格式的时间 -->
            <div class="visit-time" :class="{ 'full-time': selectedPeriod === 'earlier' }">
              {{ selectedPeriod === 'earlier' ? formatFullTime(item.footprintTime) : formatVisitTime(item.footprintTime)
              }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FootprintPage',
  data() {
    return {
      rawFootprints: [], // 原始足迹数据
      groupedFootprints: {}, // 按日期分组的足迹数据
      weekFootprints: {}, // 最近一周内的足迹（按天分组）
      earlierFootprints: [], // 一周前的足迹
      timePeriods: [], // 动态生成的时间段选项
      selectedPeriod: 'today', // 当前选中的时间段
    };
  },
  computed: {
    // 是否有足迹数据
    hasFootprints() {
      return Object.keys(this.groupedFootprints).length > 0 || this.earlierFootprints.length > 0;
    },
    // 筛选后的足迹数据
    filteredFootprints() {
      if (this.selectedPeriod === 'earlier') {
        return this.earlierFootprints;
      } else if (this.selectedPeriod === 'today') {
        return this.groupedFootprints.today || [];
      } else {
        // 最近一周内的具体日期
        return this.groupedFootprints[this.selectedPeriod] || [];
      }
    },
  },
  mounted() {
    this.getMyFootprints();
  },
  methods: {
    goToDetail(modelId) {
      const routeInfo = this.$router.resolve({
        path: '/detail',
        query: {
          id: modelId
        }
      });
      window.open(routeInfo.href, '_blank');
    },
    // 查询足迹
    getMyFootprints() {
      this.$apiFun.getMyFootprints({
        type: 2
      })
        .then(res => {
          if (res.status == 200 && res.obj && res.obj.length > 0) {
            this.rawFootprints = res.obj;
            this.processFootprintData();
          } else {
            this.rawFootprints = [];
            this.groupedFootprints = {};
            this.earlierFootprints = [];
            this.timePeriods = [];
          }
        })
        .catch(error => {
          console.error('获取足迹失败:', error);
          this.$message.error('获取足迹数据异常');
        });
    },

    // 处理足迹数据，按日期分组
    processFootprintData() {
      const today = new Date();
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(today.getDate() - 7);

      this.groupedFootprints = {};
      this.earlierFootprints = [];

      // 遍历足迹数据进行分组
      this.rawFootprints.forEach(item => {
        if (!item.footprintTime) return;

        const footprintDate = new Date(item.footprintTime);
        const footprintDateStr = this.formatDate(footprintDate);

        // 判断是否在最近一周内
        if (footprintDate >= oneWeekAgo) {
          // 最近一周内的足迹，按天分组
          const dateKey = this.getDateKey(footprintDate);
          if (!this.groupedFootprints[dateKey]) {
            this.groupedFootprints[dateKey] = [];
          }
          this.groupedFootprints[dateKey].push(item);
        } else {
          // 一周前的足迹，合并到"更早"分组
          this.earlierFootprints.push(item);
        }
      });

      // 生成时间段选项
      this.generateTimePeriods();

      // 默认选中第一个时间段
      if (this.timePeriods.length > 0) {
        this.selectedPeriod = this.timePeriods[0].key;
      }
    },

    // 生成时间段选项
    generateTimePeriods() {
      this.timePeriods = [];

      // 添加今天（如果有数据）
      if (this.groupedFootprints.today) {
        this.timePeriods.push({
          key: 'today',
          label: '今天'
        });
      }

      // 获取最近一周内的日期（排除today）
      const weekDateGroups = Object.keys(this.groupedFootprints)
        .filter(key => key !== 'today')
        .sort((a, b) => {
          // 按日期降序排列（最近的在前）
          return new Date(b) - new Date(a);
        });

      // 添加最近一周内的其他日期
      weekDateGroups.forEach(dateKey => {
        this.timePeriods.push({
          key: dateKey,
          label: this.getDateLabel(dateKey)
        });
      });

      // 如果有一周前的足迹，添加"更早"选项
      if (this.earlierFootprints.length > 0) {
        this.timePeriods.push({
          key: 'earlier',
          label: '更早'
        });
      }
    },

    // 获取日期标识（today或具体日期）
    getDateKey(date) {
      const today = new Date();
      const todayStr = this.formatDate(today);
      const dateStr = this.formatDate(date);

      if (dateStr === todayStr) {
        return 'today';
      } else {
        return dateStr;
      }
    },

    // 格式化日期为YYYY-MM-DD
    formatDate(date) {
      return `${date.getFullYear()}-${this.padZero(date.getMonth() + 1)}-${this.padZero(date.getDate())}`;
    },

    // 补零
    padZero(num) {
      return num.toString().padStart(2, '0');
    },

    // 获取日期标签（显示为"昨天"、"前天"等）
    getDateLabel(dateStr) {
      const today = new Date();
      const todayStr = this.formatDate(today);

      const targetDate = new Date(dateStr);
      const timeDiff = today.getTime() - targetDate.getTime();
      const dayDiff = Math.floor(timeDiff / (1000 * 3600 * 24));

      if (dayDiff === 1) {
        return '昨天';
      } else if (dayDiff === 2) {
        return '前天';
      } else if (dayDiff <= 7) {
        return `${dayDiff}天前`;
      } else {
        return dateStr;
      }
    },

    // 格式化访问时间（HH:MM）
    formatVisitTime(timeStr) {
      if (!timeStr) return '';
      return timeStr.split(' ')[1]?.substring(0, 5) || '';
    },

    // 格式化完整时间（YYYY-MM-DD HH:mm:ss）
    formatFullTime(timeStr) {
      if (!timeStr) return '';
      // 如果已经包含秒数，直接返回
      if (timeStr.includes(':')) {
        const parts = timeStr.split(' ');
        if (parts.length === 2 && parts[1].split(':').length === 3) {
          return timeStr;
        }
        // 如果只有时分，补充秒数
        return `${parts[0]} ${parts[1]}:00`;
      }
      // 如果格式不完整，解析为Date对象重新格式化
      const date = new Date(timeStr);
      return `${date.getFullYear()}-${this.padZero(date.getMonth() + 1)}-${this.padZero(date.getDate())} ${this.padZero(date.getHours())}:${this.padZero(date.getMinutes())}:${this.padZero(date.getSeconds())}`;
    },

    // 选择时间段
    selectPeriod(periodKey) {
      this.selectedPeriod = periodKey;
    },

    // 获取当前时间段的显示标签
    getCurrentPeriodLabel() {
      if (this.selectedPeriod === 'earlier') {
        return '更早';
      }

      const period = this.timePeriods.find(p => p.key === this.selectedPeriod);
      return period ? period.label : '暂无数据';
    },

    // 去逛逛
    goExplore() {
      this.$router.push('/models');
    }
  }
};
</script>

<style lang="less" scoped>
// 深色主题变量
@bg-primary: #2c2c2c;
@bg-secondary: #080a12;
@text-primary: #e0e0e0;
@text-secondary: #a0a0a0;
@accent-blue: #409eff;
@border-color: #444;
@divider-color: #4a4a4a;

.footprint-page {
  color: @text-primary;
  min-height: 100vh;
  padding: 20px;
  box-sizing: border-box;

  // 页面头部
  .page-header {
    display: flex;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 1px solid @border-color;

    .icon-time {
      width: 20px;
      height: 20px;
      margin-right: 10px;
      background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024"><path fill="%23e0e0e0" d="M512 896a384 384 0 1 0 0-768 384 384 0 0 0 0 768zm0 64a448 448 0 1 1 0-896 448 448 0 0 1 0 896zm-64-294.4v-192a64 64 0 0 1 128 0v192a32 32 0 0 1-64 0v-128a32 32 0 0 0-64 0v128a32 32 0 0 1-64 0z"/></svg>') no-repeat center;
    }

    span {
      font-size: 18px;
      font-weight: 500;
    }
  }

  // 无足迹提示
  .empty-footprint {
    text-align: center;
    padding: 80px 0;

    .empty-img {
      width: 150px;
      height: 150px;
      opacity: 0.5;
      margin-bottom: 20px;
    }

    p {
      color: @text-secondary;
      margin-bottom: 30px;
      font-size: 16px;
    }

    .explore-btn {
      background-color: @accent-blue;
      color: white;
      border: none;
      padding: 10px 20px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
    }
  }

  // 主内容区布局
  .main-container {
    display: flex;
    gap: 20px;
    height: calc(100vh - 100px);
    overflow: hidden;
  }

  // 左侧时间轴
  .time-axis {
    width: 120px;
    background-color: @bg-secondary;
    border-radius: 4px;
    padding: 15px 0;
    overflow-y: auto;

    .time-item {
      display: flex;
      align-items: center;
      padding: 12px 15px;
      cursor: pointer;
      transition: all 0.3s;
      position: relative;

      &::after {
        content: '';
        position: absolute;
        left: 25px;
        top: 40px;
        width: 1px;
        height: calc(100% - 40px);
        background-color: @divider-color;
      }

      &:last-child::after {
        display: none;
      }

      &.active {
        background-color: rgba(64, 158, 255, 0.1);
        color: @accent-blue;
      }

      &:hover:not(.active) {
        background-color: #454545;
      }

      .time-dot {
        width: 12px;
        height: 12px;
        border-radius: 10%;
        background-color: @text-secondary;
        margin-right: 10px;
        flex-shrink: 0;
      }

      .time-text {
        font-size: 14px;
        color: inherit;
      }
    }

    .time-item.active .time-dot {
      background-color: @accent-blue;
    }
  }

  // 右侧足迹内容区
  .footprint-content {
    flex: 1;
    background-color: @bg-secondary;
    border-radius: 4px;
    padding: 20px;
    overflow-y: auto;

    .date-label {
      font-size: 16px;
      font-weight: 500;
      margin-bottom: 15px;
      padding-bottom: 8px;
      border-bottom: 1px solid @divider-color;
    }

    // 暂无数据提示
    .no-data-tip {
      text-align: center;
      padding: 50px 0;
      color: @text-secondary;
    }

    // 模型网格
    .model-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
      gap: 15px;

      .model-item {
        background-color: @bg-primary;
        border-radius: 4px;
        overflow: hidden;
        transition: all 0.3s;
        position: relative;
        cursor: pointer;

        &:hover {
          transform: translateY(-3px);
          box-shadow: 0 3px 6px rgba(0, 0, 0, 0.3);
        }

        .model-img {
          width: 100%;
          height: 150px;
          object-fit: cover;
          display: block;
        }

        .model-meta {
          padding: 10px;
          font-size: 14px;

          .model-type {
            display: inline-block;
            background-color: #4a4a4a;
            color: @text-secondary;
            font-size: 12px;
            padding: 2px 6px;
            border-radius: 3px;
            margin-right: 5px;
          }

          .model-name {
            color: @text-primary;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            display: inline-block;
            max-width: calc(100% - 40px);
          }
        }

        .visit-time {
          position: absolute;
          top: 10px;
          right: 10px;
          background-color: rgba(0, 0, 0, 0.5);
          color: #fff;
          font-size: 12px;
          padding: 2px 6px;
          border-radius: 3px;

          // 完整时间的样式调整
          &.full-time {
            font-size: 10px;
            padding: 3px 8px;
            white-space: nowrap;
            // 如果文字太长，调整位置
            right: 5px;
            top: 5px;
          }
        }
      }
    }
  }
}
</style>